import { Check, X } from "lucide-react";
import { useI18n } from "@/lib/i18n.jsx";
import { markTipSeen, noteTipAcknowledged } from "@/lib/featureTips";

/**
 * FeatureTipModal — one-time tutorial sheet shown the first time a
 * user taps a specific Add-Meal option (photo AI, describe AI,
 * barcode, manual, saved recipes).
 *
 * Design intent:
 *   • Big illustrated "hero" panel that visually communicates the DO
 *     for that feature (e.g. clear ingredients on a plate for photo
 *     AI). Falls back to the feature icon on a colored gradient when
 *     no illustration is supplied — always works OTA.
 *   • 2-3 concrete "do" bullets so the user knows what to prepare
 *     BEFORE opening the camera / scanner.
 *   • Single big primary button "فهمت — لا تظهرها مرة أخرى" that both
 *     dismisses AND continues to the actual feature. Zero friction.
 *
 * Props:
 *   featureKey  — string persisted in localStorage (see /lib/featureTips)
 *   icon        — lucide component for the hero
 *   heroGradient — tailwind gradient classes (e.g. "from-orange-400 to-rose-500")
 *   title       — bold header line
 *   subtitle    — one-liner supporting the title
 *   tips        — array of "do" strings
 *   confirmLabel  — optional override for the primary button
 *   onConfirm   — called after markTipSeen — parent should navigate
 *                 into the actual feature here
 *   onClose     — dismiss without proceeding (X button)
 */
export default function FeatureTipModal({
  featureKey,
  icon: Icon,
  heroGradient = "from-brand-400 to-orange-500",
  title,
  subtitle,
  tips = [],
  confirmLabel,
  onConfirm,
  onClose,
}) {
  const { lang } = useI18n();
  const en = lang === "en";

  const confirmOnce = () => {
    noteTipAcknowledged(featureKey);
    onConfirm?.();
  };

  const confirmForever = () => {
    markTipSeen(featureKey);
    onConfirm?.();
  };

  return (
    <div
      className="fixed inset-0 z-[130] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="feature-tip-backdrop"
    >
      {/* Small top offset (mt-16 on mobile, mt-0 on ≥sm) so the card
          sits slightly BELOW the vertical center — visually more
          balanced against the thumb-reach area on phones, and never
          hugs the bottom like a sheet. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl w-full sm:max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 fade-in duration-200 mt-16 sm:mt-0"
        dir={en ? "ltr" : "rtl"}
        data-testid={`feature-tip-${featureKey}`}
      >
        {/* Close button — small so the primary CTA gets the attention. */}
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-3 left-3 rtl:right-3 rtl:left-auto w-8 h-8 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white transition z-10"
            data-testid="feature-tip-close"
            aria-label="close"
          >
            <X className="w-4 h-4 text-neutral-700" />
          </button>

          {/* Hero panel — bold gradient with the feature icon centred.
              Deliberately tall (h-40) so the tutorial feels like a
              friendly walkthrough, not a warning. */}
          <div className={`h-40 flex items-center justify-center bg-gradient-to-br ${heroGradient} relative overflow-hidden`}>
            {/* Decorative dots for visual interest without new assets */}
            <div className="absolute top-6 left-6 rtl:right-6 rtl:left-auto w-2 h-2 rounded-full bg-white/40" />
            <div className="absolute top-12 left-16 rtl:right-16 rtl:left-auto w-1.5 h-1.5 rounded-full bg-white/30" />
            <div className="absolute bottom-8 right-8 rtl:left-8 rtl:right-auto w-2 h-2 rounded-full bg-white/40" />
            <div className="absolute bottom-14 right-16 rtl:left-16 rtl:right-auto w-1 h-1 rounded-full bg-white/30" />
            {Icon && (
              <div className="w-24 h-24 rounded-full bg-white/25 backdrop-blur flex items-center justify-center shadow-2xl">
                <Icon className="w-12 h-12 text-white" strokeWidth={1.75} />
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <h2 className="text-lg font-black text-neutral-900">{title}</h2>
            {subtitle && (
              <p className="text-sm text-neutral-500 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>

          {tips.length > 0 && (
            <ul className="space-y-2.5" data-testid="feature-tip-list">
              {tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-700 leading-relaxed">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                  </div>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-1 space-y-2">
            <button
              onClick={confirmOnce}
              className="w-full orange-gradient text-white font-black py-4 rounded-full active:scale-[0.98] shadow-brand"
              data-testid="feature-tip-confirm-btn"
            >
              {confirmLabel || (en ? "Got it" : "فهمت")}
            </button>
            <button
              onClick={confirmForever}
              className="w-full bg-neutral-100 text-neutral-600 font-semibold py-3 rounded-full active:scale-[0.98] hover:bg-neutral-200 transition-colors"
              data-testid="feature-tip-dont-repeat-btn"
            >
              {en ? "Got it — don't show again" : "فهمت ولا تكرر الشرح"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
